import React from "react";
import { useNavigate } from "react-router-dom";
import {
  LuCreditCard,
  LuRefreshCw,
  LuShieldCheck,
  LuChevronRight,
  LuCircleAlert,
  LuCheck,
  LuSettings,
} from "react-icons/lu";

import { ReactComponent as VisaSvg } from "../assets/Payment-Logos/Visa.svg";
import { ReactComponent as MastercardSvg } from "../assets/Payment-Logos/Mastercard.svg";
import { ReactComponent as AmexSvg } from "../assets/Payment-Logos/Amex.svg";
import { ReactComponent as DiscoverSvg } from "../assets/Payment-Logos/Discover-card-light.svg";

/**
 * TEMPORARY PREVIEW
 *
 * true  = show every state underneath each other
 * false = show the real membership state only
 */
const SHOW_ALL_STATES = false;

const PREVIEW_STATES = [
  {
    label: "1. Good standing",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "active",
      canReserve: true,

      hasCardOnFile: false,
      membershipCardRequired: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 0,
      membershipReservationsRemaining: 3,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 0,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "2. Attendance warning · 0 of 2 attended",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "warning",
      canReserve: true,

      hasCardOnFile: false,
      membershipCardRequired: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 0,
      membershipReservationsRemaining: 3,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 1,
      reservationAttendanceStreak: 0,
      attendanceWarning: true,
      attendancesUntilReset: 2,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "3. Attendance warning · 1 of 2 attended",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "warning",
      canReserve: true,

      hasCardOnFile: false,
      membershipCardRequired: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 1,
      membershipReservationsRemaining: 2,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 1,
      reservationAttendanceStreak: 1,
      attendanceWarning: true,
      attendancesUntilReset: 1,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "4. Reservation limit reached",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "limit_reached",
      canReserve: false,

      hasCardOnFile: false,
      membershipCardRequired: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 3,
      membershipReservationsRemaining: 0,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 0,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "5. Card permanently required",
    membership: {
      membershipActive: false,
      membershipStatus: "card_required",
      reservationAccessStatus: "card_required",
      canReserve: false,

      hasCardOnFile: false,
      membershipCardRequired: true,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 0,
      membershipReservationsRemaining: 3,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 2,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "6. Required card has an issue",
    membership: {
      membershipActive: false,
      membershipStatus: "card_issue",
      reservationAccessStatus: "card_issue",
      canReserve: false,

      hasCardOnFile: true,
      membershipPaymentMethodId: "pm_preview",
      membershipCardBrand: "visa",
      membershipCardLast4: "4242",

      membershipCardRequired: true,
      membershipCardRequiresReplacement: true,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 0,
      membershipReservationsRemaining: 3,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 2,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },

  {
    label: "7. Required card saved",
    membership: {
      membershipActive: true,
      membershipStatus: "active",
      reservationAccessStatus: "active",
      canReserve: true,

      hasCardOnFile: true,
      membershipPaymentMethodId: "pm_preview",
      membershipCardBrand: "visa",
      membershipCardLast4: "4242",

      membershipCardRequired: true,
      membershipCardRequiresReplacement: false,

      membershipReservationLimit: 3,
      membershipReservationsUsed: 1,
      membershipReservationsRemaining: 2,
      membershipReservationRefillsAt: "2026-08-14T12:00:00.000Z",

      membershipNoShowStrikes: 2,
      reservationAttendanceStreak: 0,
      attendanceWarning: false,

      membershipNoShowFeeAmount: 10,
      membershipLateCancelFeeAmount: 10,
      membershipLateCancelHours: 6,
    },
  },
];

export default function CardRequiredReservationBanner({
  membership,
  onAddCard,
  hideWhenActive = false,
  style = {},
}) {
  const navigate = useNavigate();

  if (SHOW_ALL_STATES) {
    return (
      <>
        <MotionStyles />

        <div style={S.previewWrap}>
          {PREVIEW_STATES.map((preview) => (
            <div key={preview.label} style={S.previewState}>
              <div style={S.previewLabel}>{preview.label}</div>

              <ReservationState
                membership={preview.membership}
                onAddCard={onAddCard}
                hideWhenActive={false}
                navigate={navigate}
                style={style}
              />
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <MotionStyles />

      <ReservationState
        membership={membership}
        onAddCard={onAddCard}
        hideWhenActive={hideWhenActive}
        navigate={navigate}
        style={style}
      />
    </>
  );
}

function ReservationState({
  membership,
  onAddCard,
  hideWhenActive,
  navigate,
  style,
}) {
  if (!membership) {
    return null;
  }

  const status = String(
    membership?.membershipStatus || ""
  ).toLowerCase();

  const reservationAccessStatus = String(
    membership?.reservationAccessStatus || ""
  ).toLowerCase();

  const hasCardOnFile = Boolean(
    membership?.hasCardOnFile ||
      membership?.membershipPaymentMethodId ||
      membership?.membershipCardLast4
  );

  const membershipCardRequired =
    membership?.membershipCardRequired === true;

  const isCardIssue =
    reservationAccessStatus === "card_issue" ||
    membership?.membershipCardRequiresReplacement === true ||
    status === "card_issue" ||
    status === "past_due" ||
    status === "unpaid";

  const reservationLimit = Number(
    membership?.membershipReservationLimit || 3
  );

  const reservationsUsed = Number(
    membership?.membershipReservationsUsed || 0
  );

  const reservationsRemaining = Number.isFinite(
    Number(membership?.membershipReservationsRemaining)
  )
    ? Math.max(
        0,
        Number(membership.membershipReservationsRemaining)
      )
    : Math.max(0, reservationLimit - reservationsUsed);

  const attendanceStreak = Math.max(
    0,
    Math.min(
      2,
      Number(
        membership?.reservationAttendanceStreak || 0
      )
    )
  );

  const attendanceWarning =
    reservationAccessStatus === "warning" ||
    membership?.attendanceWarning === true ||
    (
      Number(membership?.membershipNoShowStrikes || 0) === 1 &&
      !membershipCardRequired
    );

  const accessStatus =
    reservationAccessStatus ||
    deriveAccessStatus({
      membership,
      hasCardOnFile,
      membershipCardRequired,
      isCardIssue,
      attendanceWarning,
      reservationsRemaining,
    });

  const isActive =
    accessStatus === "active";

  const isLimitReached =
    accessStatus === "limit_reached";

  const isCardRequired =
    accessStatus === "card_required";

  if (
    hideWhenActive &&
    isActive
  ) {
    return null;
  }

  const noShowFee = Number(
    membership?.membershipNoShowFeeAmount ?? 10
  );

  const lateCancelFee = Number(
    membership?.membershipLateCancelFeeAmount ??
      noShowFee
  );

  const lateCancelHours = Number(
    membership?.membershipLateCancelHours ?? 6
  );

  const lateCancelLabel =
    `${lateCancelHours} hour${
      lateCancelHours === 1 ? "" : "s"
    }`;

  const refillsOn = formatShortRefillDate(
    membership?.membershipReservationRefillsAt
  );

  const cardBrand = String(
    membership?.membershipCardBrand || ""
  )
    .toLowerCase()
    .trim();

  const cardLast4 = String(
    membership?.membershipCardLast4 || ""
  ).trim();

  const handleSettingsClick = () => {
    navigate("/membership");
  };

  const handleTicketsClick = () => {
    navigate("/tickets");
  };

  const handleCardClick = () => {
    if (typeof onAddCard === "function") {
      onAddCard();
      return;
    }

    navigate("/membership", {
      state: {
        intent: isCardIssue
          ? "update_card"
          : isCardRequired
          ? "add_card"
          : "view_settings",
      },
    });
  };

  /**
   * States 1, 4 and 7.
   */
  if (
    isActive ||
    isLimitReached
  ) {
    const requiredCardSaved =
      membershipCardRequired &&
      hasCardOnFile;

    return (
      <div
        style={{
          ...S.activeBanner,
          ...(isLimitReached
            ? S.limitBanner
            : {}),
          ...style,
        }}
      >
        <div
          style={{
            ...S.activeIcon,
            ...(isLimitReached
              ? S.limitIcon
              : {}),
          }}
        >
          <span
            aria-hidden="true"
            style={{
              ...S.greenPulseDot,
              ...(isLimitReached
                ? S.limitPulseDot
                : {}),
            }}
          />

          {isLimitReached ? (
            <LuRefreshCw
              size={17}
              strokeWidth={2.5}
            />
          ) : (
            <LuCheck
              size={17}
              strokeWidth={2.5}
            />
          )}
        </div>

        <div style={S.activeContent}>
          <div style={S.activeTextWrapper}>
            <div style={S.activeHeader}>
              {isLimitReached
                ? "Reservation limit reached"
                : "Ready to reserve"}
            </div>

            <span style={S.activeText}>
              <span style={S.activeReservationCount}>
                {reservationsRemaining}/{reservationLimit}
              </span>{" "}
              reservations left
            </span>

            {requiredCardSaved ? (
              <div style={S.savedCardMeta}>
                <span
                  style={{
                    ...S.miniCard,
                    ...getBrandBg(cardBrand),
                  }}
                >
                  <CardBrandLogo brand={cardBrand} />
                </span>

                <span style={S.savedCardNumber}>
                  •••• {cardLast4 || "••••"}
                </span>

                {refillsOn && (
                  <>
                    <span style={S.savedCardDot}>·</span>

                    <span style={S.savedCardRefill}>
                      Refills {refillsOn}
                    </span>
                  </>
                )}
              </div>
            ) : (
              refillsOn && (
                <div style={S.activeMeta}>
                  <span style={S.activeRefillLine}>
                    <LuRefreshCw
                      size={12}
                      strokeWidth={2.4}
                    />

                    {isLimitReached
                      ? `Reservations will refill ${refillsOn}`
                      : `Refills ${refillsOn}`}
                  </span>
                </div>
              )
            )}
          </div>
        </div>

        <div style={S.activeActions}>
          <button
            type="button"
            style={S.activeActionButton}
            onClick={(event) => {
              event.stopPropagation();
              handleSettingsClick();
            }}
            aria-label="Reservation settings"
          >
            <LuSettings
              size={15}
              strokeWidth={2.5}
            />
          </button>

          <button
            type="button"
            style={S.activeButton}
            onClick={(event) => {
              event.stopPropagation();
              handleTicketsClick();
            }}
            aria-label="View tickets"
          >
            <LuChevronRight
              size={17}
              strokeWidth={2.6}
            />
          </button>
        </div>
      </div>
    );
  }

  /**
   * States 2 and 3.
   *
   * Single slim progress bar.
   * Attendance count sits below the bar.
   */
  if (attendanceWarning) {
    const attendancesRemaining = Math.max(
      0,
      2 - attendanceStreak
    );

    const progressPercent = Math.min(
      100,
      Math.max(
        0,
        (attendanceStreak / 2) * 100
      )
    );

const warningTitle =
  attendancesRemaining === 1
    ? "You no-showed. Attend 1 more gathering to clear your warning."
    : "You no-showed. Attend your next 2 gatherings to clear your warning.";

    const warningBody =
      attendancesRemaining === 1
        ? "You can still reserve normally. Show up to your next gathering to return your account to good standing."
        : "You can still reserve normally. Show up to your next two gatherings in a row to return your account to good standing.";

    return (
      <section
        style={{
          ...S.card,
          ...S.attendanceWarningCard,
          ...style,
        }}
      >
        <div style={S.topRow}>
          <div
            style={{
              ...S.iconCircle,
              ...S.attendanceWarningIcon,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                ...S.redPulseDot,
                ...S.amberPulseDot,
              }}
            />

            <LuCircleAlert
              size={15}
              strokeWidth={2.6}
            />
          </div>

          <div style={S.eyebrow}>
            Attendance warning
          </div>
        </div>

        <h2 style={S.title}>
          {warningTitle}
        </h2>

        <p style={S.body}>
          {warningBody}
        </p>

        <div style={S.warningProgress}>
          <div style={S.warningProgressHeader}>
            <span style={S.warningProgressLabel}>
              Attendance progress
            </span>
          </div>

          <div
            style={S.warningProgressTrack}
            role="progressbar"
            aria-label={`${attendanceStreak} of 2 gatherings attended`}
            aria-valuemin={0}
            aria-valuemax={2}
            aria-valuenow={attendanceStreak}
          >
            <div
              style={{
                ...S.warningProgressFill,
                width: `${progressPercent}%`,
              }}
            />
          </div>

          <div style={S.warningProgressMeta}>
            <span style={S.warningProgressCount}>
              {attendanceStreak} of 2 attended
            </span>
          </div>
        </div>

        <div style={S.warningConsequence}>
          <LuCircleAlert
            size={13}
            strokeWidth={2.35}
            style={S.warningConsequenceIcon}
          />

          <span>
            Another no-show before this warning is cleared will
            permanently require a card for future reservations.
          </span>
        </div>

        <button
          type="button"
          style={S.ctaButton}
          onClick={handleSettingsClick}
        >
          <span>View details</span>

          <LuChevronRight
            size={17}
            strokeWidth={2.6}
          />
        </button>
      </section>
    );
  }

  /**
   * States 5 and 6.
   */
  const copy = isCardIssue
    ? {
        eyebrow: "Card update needed",
        title: "Update your card to keep reserving",
        body:
          "Your account requires a card, but the saved card needs attention. Update it before making another reservation.",
        cta: "Update card",
        secondaryTitle: "Card issue",
        secondaryText: "Needs attention",
      }
    : {
        eyebrow: "Reservation access paused",
        title: "Add a card to continue",
        body:
          "After repeated no-shows, your account now permanently requires a card for future reservations.",
        cta: "Add card",
        secondaryTitle: "Card required",
        secondaryText: "To reserve",
      };

const feePolicy = isCardIssue
  ? `Your upcoming reservations were released. Update your card to reserve again. Future no-shows or cancellations within ${lateCancelLabel} cost $${formatMoney(
      noShowFee
    )}.`
  : `Your upcoming reservations were released. Add a card to reserve again. Future no-shows or cancellations within ${lateCancelLabel} cost $${formatMoney(
      noShowFee
    )}.`;

  return (
    <section
      style={{
        ...S.card,
        ...S.warningCard,
        ...style,
      }}
    >
      <div style={S.topRow}>
        <div
          style={{
            ...S.iconCircle,
            ...S.warningIconCircle,
          }}
        >
          <span
            aria-hidden="true"
            style={S.redPulseDot}
          />

          {isCardIssue ? (
            <LuCircleAlert
              size={15}
              strokeWidth={2.6}
            />
          ) : (
            <LuCreditCard
              size={15}
              strokeWidth={2.6}
            />
          )}
        </div>

        <div style={S.eyebrow}>
          {copy.eyebrow}
        </div>
      </div>

      <h2 style={S.title}>
        {copy.title}
      </h2>

      <p style={S.body}>
        {copy.body}
      </p>

      <div style={S.infoGrid}>
        <div style={S.infoCard}>
          <div style={S.infoIcon}>
            <LuCheck
              size={17}
              strokeWidth={2.35}
            />
          </div>

          <div>
            <div style={S.infoTitle}>
              Access restores
            </div>

            <div style={S.infoText}>
              {isCardIssue ? "After update" : "After setup"}
            </div>
          </div>
        </div>

        <div
          style={S.infoDivider}
          aria-hidden="true"
        />

        <div style={S.infoCard}>
          {isCardIssue ? (
            <>
              <span
                style={{
                  ...S.miniCard,
                  ...getBrandBg(cardBrand),
                  ...S.miniCardIssue,
                }}
              >
                <CardBrandLogo brand={cardBrand} />
              </span>

              <div>
                <div style={S.infoTitle}>
                  •••• {cardLast4 || "••••"}
                </div>

                <div style={S.infoText}>
                  Needs attention
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={S.infoIcon}>
                <LuShieldCheck
                  size={17}
                  strokeWidth={2.35}
                />
              </div>

              <div>
                <div style={S.infoTitle}>
                  Card required
                </div>

                <div style={S.infoText}>
                  To reserve
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={S.warningConsequence}>
        <LuCircleAlert
          size={13}
          strokeWidth={2.35}
          style={S.warningConsequenceIcon}
        />

        <span>{feePolicy}</span>
      </div>

      <button
        type="button"
        style={S.ctaButton}
        onClick={handleCardClick}
      >
        <span>{copy.cta}</span>

        <LuChevronRight
          size={17}
          strokeWidth={2.6}
        />
      </button>
    </section>
  );
}

function CardBrandLogo({ brand }) {
  if (brand === "visa") {
    return <VisaSvg style={S.paymentCardSvg} />;
  }

  if (brand === "mastercard") {
    return <MastercardSvg style={S.paymentCardSvg} />;
  }

  if (brand === "amex") {
    return <AmexSvg style={S.paymentCardSvg} />;
  }

  if (brand === "discover") {
    return <DiscoverSvg style={S.paymentCardSvg} />;
  }

  return (
    <LuCreditCard
      size={13}
      strokeWidth={2.2}
    />
  );
}

function getBrandBg(brand) {
  return {
    backgroundColor:
      brand === "mastercard"
        ? "#252525"
        : brand === "amex"
        ? "#2F6DC8"
        : "#FFFFFF",
  };
}

function deriveAccessStatus({
  membership,
  hasCardOnFile,
  membershipCardRequired,
  isCardIssue,
  attendanceWarning,
  reservationsRemaining,
}) {
  if (isCardIssue) {
    return "card_issue";
  }

  if (
    membershipCardRequired &&
    !hasCardOnFile
  ) {
    return "card_required";
  }

  if (attendanceWarning) {
    return "warning";
  }

  if (reservationsRemaining <= 0) {
    return "limit_reached";
  }

  if (
    membership?.membershipActive === true
  ) {
    return "active";
  }

  return "card_required";
}

function formatMoney(value) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return "0";
  }

  return Number.isInteger(parsed)
    ? String(parsed)
    : parsed.toFixed(2);
}

function formatShortRefillDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
  });
}

function MotionStyles() {
  return (
    <style>
      {`
        @keyframes tribeStatusPulseGreen {
          0% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.36);
          }

          70% {
            box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
          }

          100% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
          }
        }

        @keyframes tribeStatusPulseRed {
          0% {
            box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.32);
          }

          70% {
            box-shadow: 0 0 0 6px rgba(220, 38, 38, 0);
          }

          100% {
            box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
          }
        }

        @keyframes tribeStatusPulseAmber {
          0% {
            box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.32);
          }

          70% {
            box-shadow: 0 0 0 6px rgba(217, 119, 6, 0);
          }

          100% {
            box-shadow: 0 0 0 0 rgba(217, 119, 6, 0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}
    </style>
  );
}

const S = {
  previewWrap: {
    display: "grid",
    gap: 28,
    width: "100%",
    marginBottom: 30,
  },

  previewState: {
    minWidth: 0,
  },

  previewLabel: {
    margin: "0 0 8px 3px",
    color: "#777",
    fontSize: 11,
    lineHeight: 1,
    fontWeight: 800,
    letterSpacing: "0.055em",
    textTransform: "uppercase",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  activeBanner: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 8px",
    margin: 0,
    background: "rgba(255,255,255,.55)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: ".5px solid #ddd",
    borderRadius: 12,
    boxShadow:
      "0 2px 10px rgba(92, 92, 92, 0.08)",
    position: "relative",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  limitBanner: {
    background: "#fafafa",
  },

  activeIcon: {
    width: 38,
    height: 38,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0B0B0B",
    background: "#F1F3F5",
    borderRadius: 10,
    position: "relative",
    overflow: "visible",
  },

  limitIcon: {
    color: "#555",
    background: "#f3f4f6",
  },

  greenPulseDot: {
    position: "absolute",
    top: -3,
    right: -3,
    width: 8,
    height: 8,
    borderRadius: 999,
    background: "#22C55E",
    border: "2px solid #fff",
    animation:
      "tribeStatusPulseGreen 1.9s ease-out infinite",
    zIndex: 2,
  },

  limitPulseDot: {
    background: "#9ca3af",
    animation: "none",
  },

  activeContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flex: 1,
    marginLeft: 2,
    flexWrap: "nowrap",
    minWidth: 0,
  },

  activeTextWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },

  activeHeader: {
    fontSize: 13,
    fontWeight: 800,
    color: "#222",
    lineHeight: 1.3,
    marginBottom: 2,
    letterSpacing: "-0.02em",
  },

  activeText: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.35,
    color: "#555",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  activeReservationCount: {
    fontWeight: 900,
    color: "#222",
  },

  activeMeta: {
    marginTop: 3,
    display: "flex",
    alignItems: "center",
    gap: 5,
    minWidth: 0,
    fontSize: 10.8,
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#777",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  activeRefillLine: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  savedCardMeta: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 5,
    minWidth: 0,
    color: "#777",
    fontSize: 10.8,
    fontWeight: 650,
    lineHeight: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
  },

  miniCard: {
    width: 30,
    height: 20,
    flexShrink: 0,
    borderRadius: 4,
    border: "1px solid #EEF0F2",
    position: "relative",
    overflow: "hidden",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#111",
    boxShadow:
      "0 0px 2px rgba(0,0,0,.08)",
  },

  miniCardIssue: {
    border: "1px solid #FCA5A5",
  },

  paymentCardSvg: {
    width: 22,
    height: 14,
    objectFit: "contain",
    display: "block",
  },

  savedCardNumber: {
    color: "#555",
    fontWeight: 750,
  },

  savedCardDot: {
    color: "#aaa",
    fontWeight: 900,
  },

  savedCardRefill: {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  activeActions: {
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    marginLeft: 4,
    flexShrink: 0,
  },

  activeActionButton: {
    width: 30,
    height: 30,
    background: "transparent",
    color: "#777",
    border: "none",
    borderRadius: 8,
    padding: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  activeButton: {
    width: 30,
    height: 30,
    background: "transparent",
    color: "#777",
    border: "none",
    borderRadius: 8,
    padding: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  card: {
    width: "calc(100% + 4px)",
    boxSizing: "border-box",
    margin: "0 -2px",
    padding: "16px 14px",
    borderRadius: 14,
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,250,250,1) 100%)",
    border:
      "1px solid rgba(15,23,42,0.07)",
    boxShadow:
      "0 8px 22px rgba(15,23,42,0.035)",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  warningCard: {
    background: "#ffff",
    border: "1px solid #ddd",
  },

  attendanceWarningCard: {
    background: "#ffff",
    border: "1px solid #dddd",
  },

  topRow: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    marginBottom: 14,
  },

  iconCircle: {
    width: 26,
    height: 26,
    borderRadius: 999,
    background: "#0B0B0B",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    position: "relative",
    overflow: "visible",
  },

  redPulseDot: {
    position: "absolute",
    top: -4,
    right: -4,
    width: 8,
    height: 8,
    borderRadius: 999,
    background: "#DC2626",
    border: "2px solid #fff",
    animation:
      "tribeStatusPulseRed 1.9s ease-out infinite",
    zIndex: 2,
  },

  amberPulseDot: {
    background: "#d97706",
    animation:
      "tribeStatusPulseAmber 1.9s ease-out infinite",
  },

  warningIconCircle: {
    background: "#B91C1C",
  },

  attendanceWarningIcon: {
    background: "#d97706",
  },

  eyebrow: {
    color: "#171717",
    fontSize: 13,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: "-0.035em",
  },

  title: {
    margin: 0,
    color: "#050505",
    fontSize:
      "clamp(.4rem, 4.5vw, 1.45rem)",
    lineHeight: 1.02,
    fontWeight: 820,
    letterSpacing: "-0.05em",
  },

  body: {
    margin: "9px 0 0",
    maxWidth: 390,
    color: "#5F6673",
    fontSize: 12.5,
    lineHeight: 1.34,
    fontWeight: 540,
    letterSpacing: "-0.025em",
  },

  warningProgress: {
    marginTop: 16,
  },

  warningProgressHeader: {
    display: "flex",
    alignItems: "center",
  },

  warningProgressLabel: {
    color: "#555",
    fontSize: 10.5,
    lineHeight: 1,
    fontWeight: 750,
    letterSpacing: "0.055em",
    textTransform: "uppercase",
  },

  warningProgressCount: {
    color: "#111",
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: "-0.025em",
    whiteSpace: "nowrap",
  },

  warningProgressTrack: {
    width: "100%",
    height: 5,
    marginTop: 9,
    borderRadius: 999,
    background:
      "rgba(16, 74, 24, 0.11)",
    overflow: "hidden",
  },

  warningProgressFill: {
    height: "100%",
    borderRadius: 999,
    background: "#197f2eff",
    transition:
      "width 220ms ease",
  },

  warningProgressMeta: {
    display: "flex",
    alignItems: "center",
    marginTop: 7,
  },

  warningProgressDivider: {
    color: "#B8B0A4",
    fontWeight: 900,
  },

  warningConsequence: {
    display: "flex",
    alignItems: "flex-start",
    gap: 7,
    marginTop: 13,
    paddingTop: 12,
    borderTop:
      "1px solid #dddd",
    color: "#555",
    fontSize: 11.8,
    lineHeight: 1.35,
    fontWeight: 650,
    letterSpacing: "-0.018em",
  },

  warningConsequenceIcon: {
    color: "#555",
    flexShrink: 0,
    marginTop: 1,
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1px 1fr",
    alignItems: "center",
    gap: 12,
    marginTop: 15,
    padding: "2px 0",
  },

  infoCard: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  infoDivider: {
    width: 1,
    height: 30,
    background: "#E2E2E2",
    borderRadius: 999,
  },

  infoIcon: {
    width: 25,
    height: 25,
    color: "#0B0B0B",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  infoTitle: {
    color: "#050505",
    fontSize: 12.5,
    lineHeight: 1.05,
    fontWeight: 800,
    letterSpacing: "-0.04em",
    whiteSpace: "nowrap",
  },

  infoText: {
    marginTop: 3,
    color: "#6B7280",
    fontSize: 11.5,
    lineHeight: 1,
    fontWeight: 650,
    letterSpacing: "-0.025em",
  },

  ctaButton: {
    width: "100%",
    height: 45,
    marginTop: 13,
    borderRadius: 10,
    border:
      "1px solid #0B0B0B",
    background: "#0B0B0B",
    color: "#fff",
    fontSize: 13.5,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: "-0.035em",
    display: "grid",
    gridTemplateColumns:
      "1fr auto",
    alignItems: "center",
    padding: "0 17px",
    cursor: "pointer",
  },
};